const DatabaseManager = (function () {
  let instance;

  function createInstance() {
    const dbName = 'TaskFlow';
    const dbVersion = 1;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(dbName, dbVersion);

      request.onupgradeneeded = function (event) {
        const db = event.target.result;
        createObjectStores(db);
      };

      request.onsuccess = function (event) {
        const db = event.target.result;
        resolve(db);
      };

      request.onerror = function (event) {
        reject(event.target.error);
      };
    });
  }

  function createObjectStores(db) {
    if (!db.objectStoreNames.contains('notes')) {
      const notesStore = db.createObjectStore('notes', { keyPath: 'id' });

      notesStore.createIndex('id', 'id', { unique: true });
      notesStore.createIndex('title', 'title', { unique: false });
      notesStore.createIndex('description', 'description', { unique: false });
      notesStore.createIndex('tag', 'tag', { unique: false });
      notesStore.createIndex('favorited', 'favorited', { unique: false });
      notesStore.createIndex('date', 'date', { unique: false });
    }

    if (!db.objectStoreNames.contains('event')) {
      const eventStore = db.createObjectStore('event', { keyPath: 'id' });

      eventStore.createIndex('id', 'id', { unique: true });
      eventStore.createIndex('title', 'title', { unique: false });
      eventStore.createIndex('start', 'start', { unique: false });
      eventStore.createIndex('end', 'end', { unique: false });
      eventStore.createIndex('extendedprops', 'extendedprops', {
        unique: false,
      });
      eventStore.createIndex('url', 'url', { unique: false });
    }

    if (!db.objectStoreNames.contains('todo')) {
      const toDoStore = db.createObjectStore('todo', { keyPath: 'id' });

      toDoStore.createIndex('id', 'id', { unique: true });
      toDoStore.createIndex('title', 'title', { unique: false });
      toDoStore.createIndex('description', 'description', {
        unique: false,
      });
      toDoStore.createIndex('priority', 'priority', { unique: false });
      toDoStore.createIndex('important', 'important', { unique: false });
      toDoStore.createIndex('completed', 'completed', { unique: false });
      toDoStore.createIndex('trash', 'trash', { unique: false });
      toDoStore.createIndex('date', 'date', { unique: false });
    }

    if (!db.objectStoreNames.contains('scrumboard')) {
      const scrumStore = db.createObjectStore('scrumboard', {
        keyPath: 'id',
      });

      scrumStore.createIndex('id', 'id', { unique: true });
      scrumStore.createIndex('title', 'title', { unique: false });
      scrumStore.createIndex('description', 'description', {
        unique: false,
      });
      scrumStore.createIndex('tasksection', 'tasksection', { unique: false });
      scrumStore.createIndex('order', 'order', { unique: false });
      scrumStore.createIndex('progress', 'progress', { unique: false });
      scrumStore.createIndex('date', 'date', { unique: false });
    }

    if (!db.objectStoreNames.contains('scrumlist')) {
      const scrumList = db.createObjectStore('scrumlist', {
        keyPath: 'id',
      });

      const timestamp = new Date().getTime();

      scrumList.createIndex('id', 'id', { unique: true });
      scrumList.createIndex('title', 'title', { unique: false });
      scrumList.createIndex('order', 'order', { unique: false });
      scrumList.createIndex('timestamp', 'timestamp', { unique: false });

      const initialContainerOrders = [
        {
          id: 's-new',
          title: 'New',
          order: 0,
          timestamp: timestamp,
        },
        {
          id: 's-in-progress',
          title: 'In Progress',
          order: 1,
          timestamp: timestamp,
        },
        {
          id: 's-complete',
          title: 'Complete',
          order: 2,
          timestamp: timestamp,
        },
      ];

      initialContainerOrders.forEach(orderData => {
        scrumList.add(orderData);
      });
    }
  }

  return {
    getInstance: function () {
      if (!instance) {
        instance = createInstance();
      }
      return instance;
    },
  };
})();

// const dbName = 'TaskFlow';
// const request = indexedDB.open(dbName);

// request.onsuccess = function (event) {
//   const db = event.target.result;
//   db.close();

//   deleteDatabase();
// };

// const deleteDatabase = function () {
//   const dbName = 'TaskFlow';
//   const request = indexedDB.deleteDatabase(dbName);

//   request.onsuccess = function (event) {
//     console.log('Database deleted successfully!');
//   };

//   request.onerror = function (event) {
//     console.error('Error deleting database:', event.target.error);
//   };
// };

export const addItemtoDatabase = async function (data, objectStoreName) {
  try {
    if (!data.timestamp) {
      data.timestamp = new Date().getTime();
    } else if (typeof data.timestamp === 'string') {
      data.timestamp = new Date(data.timestamp).getTime();
    }

    const db = await DatabaseManager.getInstance();
    const transaction = db.transaction([objectStoreName], 'readwrite');
    const objectStore = transaction.objectStore(objectStoreName);

    return new Promise((resolve, reject) => {
      const request = objectStore.add(data);

      request.onsuccess = function (event) {
        resolve(data);
      };

      request.onerror = function (event) {
        console.error('Error adding item:', event.target.error);
        reject(event.target.error);
      };
    });
  } catch (err) {
    throw err;
  }
};

export const getAllItemsFromDatabase = async function (
  objectStoreName,
  filterFunction
) {
  try {
    const db = await DatabaseManager.getInstance();
    const transaction = db.transaction([objectStoreName], 'readonly');
    const objectStore = transaction.objectStore(objectStoreName);

    return new Promise((resolve, reject) => {
      const items = [];
      const request = objectStore.openCursor();

      request.onsuccess = function (event) {
        const cursor = event.target.result;
        if (cursor) {
          // Check if filtering is enabled and filter by container if necessary
          if (!filterFunction || filterFunction(cursor.value)) {
            items.push(cursor.value);
          }

          cursor.continue();
        } else {
          resolve(items);
        }
      };

      request.onerror = function (event) {
        console.error('Error getting items:', event.target.error);
        reject(event.target.error);
      };
    });
  } catch (err) {
    throw err;
  }
};

export const getItemFromDatabase = async function (id, objectStoreName) {
  try {
    const db = await DatabaseManager.getInstance();
    const transaction = db.transaction([objectStoreName], 'readonly');
    const objectStore = transaction.objectStore(objectStoreName);

    return new Promise((resolve, reject) => {
      const request = objectStore.get(id);

      request.onsuccess = function (event) {
        const result = event.target.result;
        resolve(result);
      };

      request.onerror = function (event) {
        console.error('Error getting item:', event.target.error);
        reject(event.target.error);
      };
    });
  } catch (err) {
    throw err;
  }
};

export const deleteItemFromDatabase = async function (
  objectId,
  objectStoreName
) {
  try {
    const db = await DatabaseManager.getInstance();
    const transaction = db.transaction([objectStoreName], 'readwrite');
    const objectStore = transaction.objectStore(objectStoreName);

    return new Promise((resolve, reject) => {
      const request = objectStore.delete(objectId);

      request.onsuccess = function (e) {
        resolve(true);
      };

      request.onerror = function (e) {
        console.error('Error getting item:', e.target.error);
        reject(e.target.error);
      };
    });
  } catch (err) {
    throw err;
  }
};

export const deleteMultipleItemsFromDatabase = async function (
  objectIds,
  objectStoreName
) {
  try {
    const db = await DatabaseManager.getInstance();
    const transaction = db.transaction([objectStoreName], 'readwrite');
    const objectStore = transaction.objectStore(objectStoreName);

    return Promise.all(
      objectIds.map(objectId => {
        return new Promise((resolve, reject) => {
          const request = objectStore.delete(objectId);

          request.onsuccess = function (e) {
            resolve(true);
          };

          request.onerror = function (e) {
            console.error('Error deleting item:', e.target.error);
            reject(e.target.error);
          };
        });
      })
    );
  } catch (err) {
    throw err;
  }
};

export const getObjectStoreLength = async function (objectStoreName) {
  try {
    const db = await DatabaseManager.getInstance();
    const transaction = db.transaction([objectStoreName], 'readonly');
    const objectStore = transaction.objectStore(objectStoreName);

    return new Promise((resolve, reject) => {
      const request = objectStore.openCursor();
      let count = 0;

      request.onsuccess = function (event) {
        const cursor = event.target.result;

        if (cursor) {
          count++;
          cursor.continue();
        } else {
          resolve(count);
        }
      };

      request.onerror = function (event) {
        console.error('Error counting items:', event.target.error);
        reject(event.target.error);
      };
    });
  } catch (err) {
    throw err;
  }
};

export const updateItemInDatabase = async function (
  id,
  objectStoreName,
  newData
) {
  try {
    newData.isEdited = true;
    newData.timestamp = new Date().getTime();

    const db = await DatabaseManager.getInstance();
    const transaction = db.transaction([objectStoreName], 'readwrite');
    const objectStore = transaction.objectStore(objectStoreName);
    const request = objectStore.get(id);

    return new Promise((resolve, reject) => {
      request.onsuccess = function (event) {
        const existingData = event.target.result;

        let dataChanged = false;

        for (const property in newData) {
          if (
            newData.hasOwnProperty(property) &&
            existingData[property] !== newData[property]
          ) {
            existingData[property] = newData[property];
            // console.log(existingData[property]);
            dataChanged = true;
          }
        }

        if (dataChanged) {
          const updateRequest = objectStore.put(existingData);

          updateRequest.onsuccess = function () {
            // console.log('Item updated successfully:', existingData);
            resolve(existingData);
          };

          updateRequest.onerror = function (event) {
            console.error('Error updating item:', event.target.error);
            reject(event.target.error);
          };
        } else {
          resolve(existingData);
        }
      };

      request.onerror = function (event) {
        console.error('Error getting item to update:', event.target.error);
        reject(event.target.error);
      };
    });
  } catch (err) {
    throw err;
  }
};

export const getItemValueFromDatabase = async function (
  id,
  property,
  objectStoreName
) {
  try {
    const db = await DatabaseManager.getInstance();
    const transaction = db.transaction([objectStoreName], 'readonly');
    const objectStore = transaction.objectStore(objectStoreName);

    const request = objectStore.get(id);

    return new Promise((resolve, reject) => {
      request.onsuccess = function (event) {
        const existingData = event.target.result;

        if (existingData && existingData.hasOwnProperty(property)) {
          // console.log(existingData[property]);
          resolve(existingData[property]);
        } else {
          resolve(null);
        }
      };

      request.onerror = function (event) {
        console.error('Error getting item value:', event.target.error);
        reject(event.target.error);
      };
    });
  } catch (err) {
    throw err;
  }
};

//Delete Database

// const dbName = 'TaskFlow';
// const request = indexedDB.open(dbName);

// request.onsuccess = function (event) {
//   const db = event.target.result;
//   db.close();

//   deleteDatabase();
// };

// const deleteDatabase = function () {
//   const dbName = 'TaskFlow';
//   const request = indexedDB.deleteDatabase(dbName);

//   request.onsuccess = function (event) {
//     console.log('Database deleted successfully!');
//   };

//   request.onerror = function (event) {
//     console.error('Error deleting database:', event.target.error);
//   };
// };

// const dbName = 'TaskFlow';
// const Dbversion = 1;
// const storeNameToDelete = 'scrumboard';

// const request = indexedDB.open(dbName, Dbversion);

// request.onupgradeneeded = function (event) {
//   const db = event.target.result;
//   if (db.objectStoreNames.contains(storeNameToDelete)) {
//     db.deleteObjectStore(storeNameToDelete);
//   }
// };

// request.onsuccess = function (event) {
//   console.log('Object store deleted successfully.');
// };

// request.onerror = function (event) {
//   console.error('Error deleting object store:', event.target.error);
// };
